import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FORMAS_DE_PAGAMENTO, FORMA_DE_PAGAMENTO_LABELS, hojeEmTexto, type FormaDePagamento } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, textoParaCentavos } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";
import { PreviaDaTaxa } from "./PreviaDaTaxa";

/** Recebeu tudo ou parte do extrato: vira uma entrada paga ligada ao extrato emitido (`extratoId`)
 *  ou, nos meses antes dos extratos, ao cliente e ao mês. */
export function RegistrarRecebimento({
  clienteId,
  competencia,
  extratoId,
  saldoCents,
  estreito = false,
}: { saldoCents: number; estreito?: boolean } & ({ extratoId: string; clienteId?: undefined; competencia?: undefined } | { extratoId?: undefined; clienteId: string; competencia: string })) {
  const utils = trpc.useUtils();
  const [valor, setValor] = useState(reais(saldoCents));
  const [pagoEm, setPagoEm] = useState(hojeEmTexto());
  const [forma, setForma] = useState<FormaDePagamento | "">("PIX");
  useEffect(() => setValor(reais(saldoCents)), [saldoCents]);

  const aoRegistrar = {
    onSuccess: () => {
      toast.success("Recebimento registrado.");
      utils.financeiro.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  };
  const doMes = trpc.financeiro.aReceber.registrarRecebimento.useMutation(aoRegistrar);
  const doExtrato = trpc.financeiro.extratos.registrarRecebimento.useMutation(aoRegistrar);
  const pendente = doMes.isPending || doExtrato.isPending;
  const valorCents = textoParaCentavos(valor);

  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 dark:border-border dark:bg-muted/30">
      <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Registrar recebimento</p>
      <div className={estreito ? "grid gap-2" : "grid gap-2 sm:grid-cols-3"}>
        <label>
          <span className={rotulo}>Valor</span>
          <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" className={campo} />
        </label>
        <label>
          <span className={rotulo}>Data</span>
          <input type="date" value={pagoEm} onChange={(e) => setPagoEm(e.target.value)} className={campo} />
        </label>
        <label>
          <span className={rotulo}>Forma</span>
          <select value={forma} onChange={(e) => setForma(e.target.value as FormaDePagamento | "")} className={campo}>
            <option value="">Não informada</option>
            {FORMAS_DE_PAGAMENTO.map((f) => (
              <option key={f} value={f}>
                {FORMA_DE_PAGAMENTO_LABELS[f]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {valorCents > saldoCents && <p className="text-[11px] text-amber-600">O valor passa do saldo: o cliente fica com crédito.</p>}
      <PreviaDaTaxa valorCents={valorCents} forma={forma || null} />
      <div className="flex justify-end">
        <button
          type="button"
          disabled={valorCents <= 0 || !pagoEm || pendente}
          onClick={() => {
            const comum = { valorCents, pagoEm, formaDePagamento: forma || null };
            if (extratoId) doExtrato.mutate({ extratoId, ...comum });
            else doMes.mutate({ clienteId: clienteId!, competencia: competencia!, ...comum });
          }}
          className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          Registrar
        </button>
      </div>
    </div>
  );
}
